import { useCallback, useEffect } from "react";
import { listRecentWorkflows } from "../../../api/threads/threadsApi";
import { useLoad } from "../../../shared/loading/useLoad";
import { onThreadListChanged } from "../../threads/data/threadListEvents";
import { RECENT_LIMIT } from "./workflowGroups";

const NONE: readonly string[] = [];
const NO_CANDIDATES: Promise<readonly string[]> = Promise.resolve(NONE);

/**
 * Of the Workflow items `among`, the ones started most recently in the project, newest first,
 * as module C reads them (F123): the list asks only about those it could show as recent, so
 * its own filters never hide an older start. Read again when the project's Threads change,
 * here or in another tab, and on focus, as the Thread list is (`useProjectThreads`). Until
 * read, or if the read fails, none: a Workflow list never waits for them.
 */
export function useRecentWorkflows(projectId: string, among: readonly string[]): readonly string[] {
  // One question per set of candidates, whatever array holds them.
  const question = among.join(" ");
  const load = useCallback(
    (signal: AbortSignal) => (question === "" ? NO_CANDIDATES : listRecentWorkflows(projectId, question.split(" "), RECENT_LIMIT, signal)),
    [projectId, question],
  );
  const { state, refresh } = useLoad(load);
  useEffect(() => {
    const unsubscribe = onThreadListChanged(refresh);
    window.addEventListener("focus", refresh);
    return () => {
      unsubscribe();
      window.removeEventListener("focus", refresh);
    };
  }, [refresh]);
  return state.status === "loaded" ? state.value : NONE;
}
